import React, { useRef, useEffect } from "react";
import Matter from "matter-js";

export function AnimationServices() {
  const sceneRef = useRef(null);
  const engineRef = useRef(Matter.Engine.create());
  const renderRef = useRef(null);
  const mouseConstraintRef = useRef(null);

  useEffect(() => {
    const engine = engineRef.current;

    // Soft moon gravity
    engine.world.gravity.y = 0.01;

    // Create renderer
    const render = Matter.Render.create({
      element: sceneRef.current,
      engine: engine,
      options: {
        width: window.innerWidth - 15,
        height: 400,
        wireframes: false,
        background: "#08020200",
        showDebug: false,
        showBounds: false,
        showVelocity: false,
      },
    });
    renderRef.current = render;

    // Walls - static boundaries
    const walls = [
      Matter.Bodies.rectangle(window.innerWidth / 2, 0, window.innerWidth, 10, { isStatic: true }),
      Matter.Bodies.rectangle(window.innerWidth / 2, 400, window.innerWidth, 10, { isStatic: true }),
      Matter.Bodies.rectangle(0, 200, 10, 400, { isStatic: true }),
      Matter.Bodies.rectangle(window.innerWidth, 200, 10, 400, { isStatic: true }),
    ];
    Matter.World.add(engine.world, walls);

    // List to keep track of dynamic labeled objects (services & generated thoughts)
    const objects = [];

    // Circle - radius 90
    const circle = Matter.Bodies.circle(150, 50, 90, {
      restitution: 0.7,
      friction: 0.1,
      render: { fillStyle: "#DFE4C7" },
      labelText: "Digital Marketing",
      labelColor: "#000000"
    });
    objects.push(circle);

    // Rectangle - 160x80
    const rectangle = Matter.Bodies.rectangle(350, 50, 160, 80, {
      restitution: 0.6,
      friction: 0.1,
      render: { fillStyle: "#A8DAF4" },
      labelText: "Full Stack",
      labelColor: "#000000"
    });
    objects.push(rectangle);

    // Triangle - polygon with 3 sides, radius 80
    const triangle = Matter.Bodies.polygon(550, 50, 3, 80, {
      restitution: 0.5,
      friction: 0.1,
      render: { fillStyle: "#ABAEDB" },
      labelText: "UI/UX",
      labelColor: "#000000"
    });
    objects.push(triangle);

    // Pentagon - polygon with 5 sides, radius 60
    const pentagon = Matter.Bodies.polygon(750, 50, 5, 60, {
      restitution: 0.6,
      friction: 0.1,
      render: { fillStyle: "#B2DECA" },
      labelText: "Creative",
      labelColor: "#000000"
    });
    objects.push(pentagon);

    // Hexagon - polygon with 6 sides, radius 60
    const hexagon = Matter.Bodies.polygon(950, 50, 6, 60, {
      restitution: 0.7,
      friction: 0.1,
      render: { fillStyle: "#92A8D1" },
      labelText: "RPA",
      labelColor: "#000000"
    });
    objects.push(hexagon);

    // Large circle - radius 100
    const largeCircle = Matter.Bodies.circle(1150, 50, 100, {
      restitution: 0.8,
      friction: 0.1,
      render: { fillStyle: "#C4A8D6" },
      labelText: "Marketing Analysis",
      labelColor: "#000000"
    });
    objects.push(largeCircle);

    // --- AI ADVANCED COMPOUND OBJECT ---
    const aiX = window.innerWidth / 2;
    const aiY = 200;
    const aiColor = "#8B5CF6"; // Indigo/Purple
    const aiParts = [];

    // Core circle
    const core = Matter.Bodies.circle(aiX, aiY, 40, {
      render: { fillStyle: aiColor }
    });
    aiParts.push(core);

    // Rotated intersecting shapes to build a sleek 8-pointed star
    const numCrosses = 3;
    const rectWidth = 120;
    const rectHeight = 25;
    for (let i = 0; i < numCrosses; i++) {
      const angle = (i * Math.PI) / numCrosses;
      const rect = Matter.Bodies.rectangle(aiX, aiY, rectWidth, rectHeight, {
        angle: angle,
        render: { fillStyle: aiColor }
      });
      aiParts.push(rect);
    }

    // Combine individual pieces into one cohesive compound body
    const aiBody = Matter.Body.create({
      parts: aiParts,
      restitution: 0.6,
      friction: 0.1,
      labelText: "AI",
      labelColor: "#FFFFFF"
    });
    objects.push(aiBody);

    Matter.World.add(engine.world, objects);

    // Add mouse control for dragging bodies
    const mouse = Matter.Mouse.create(render.canvas);
    const mouseConstraint = Matter.MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });
    mouseConstraintRef.current = mouseConstraint;
    Matter.World.add(engine.world, mouseConstraint);

    render.mouse = mouse;

    // Run engine and renderer
    Matter.Engine.run(engine);
    Matter.Render.run(render);

    // Setup for thought bubble creation
    let thoughtCooldown = 0;
    const thoughtWords = ["Idea", "Logic", "Insight", "Future", "Concept", "Smart", "Growth", "Scale", "Data"];

    // --- INTERACTION & THOUGHT BUBBLE PHYSICS ENGINE EVENT ---
    Matter.Events.on(engine, "afterUpdate", () => {
      // Handle cooldown between thought generations
      if (thoughtCooldown > 0) {
        thoughtCooldown--;
      }

      const aiPos = aiBody.position;
      const allBodies = Matter.Composite.allBodies(engine.world);

      allBodies.forEach((body) => {
        // 1. Handle floating behavior & lifespan for existing thoughts
        if (body.isThought) {
          // Continuous light anti-gravity push so they rise gracefully
          Matter.Body.applyForce(body, body.position, {
            x: (Math.random() - 0.5) * 0.00005,
            y: -0.0004 * body.mass
          });

          body.lifespan--;

          // Start shrinking when close to disappearing
          if (body.lifespan < 30) {
            Matter.Body.scale(body, 0.9, 0.9);
          }

          // Fully remove when expired
          if (body.lifespan <= 0) {
            Matter.Composite.remove(engine.world, body);
            const index = objects.indexOf(body);
            if (index > -1) {
              objects.splice(index, 1);
            }
          }
          return;
        }

        // 2. Identify proximity to other services to trigger "Creation of Thoughts"
        if (body.isStatic || body === aiBody || aiBody.parts.includes(body)) return;

        const dx = aiPos.x - body.position.x;
        const dy = aiPos.y - body.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        const spawnDistance = 160; // Proximity threshold

        if (distance < spawnDistance && thoughtCooldown === 0) {
          // Define a spawn coordinate midway between the AI and the service
          const spawnX = (aiPos.x + body.position.x) / 2;
          const spawnY = (aiPos.y + body.position.y) / 2 - 20;

          const randomWord = thoughtWords[Math.floor(Math.random() * thoughtWords.length)];

          // Build a lightweight thought bubble
          const thoughtBubble = Matter.Bodies.circle(spawnX, spawnY, 35, {
            restitution: 0.8,
            frictionAir: 0.01,
            render: {
              fillStyle: "#E2F1FF", // Pastel blue
              strokeStyle: "#7DD3FC", // Accent border
              lineWidth: 2
            },
            labelText: randomWord,
            labelColor: "#0369A1", // Solid dark sky blue
            isThought: true,
            lifespan: 180 // Float for ~3 seconds
          });

          // Propel the thought slightly upwards and outwards
          Matter.Body.setVelocity(thoughtBubble, {
            x: (Math.random() - 0.5) * 4,
            y: -2.5
          });

          // Register and introduce to world
          objects.push(thoughtBubble);
          Matter.Composite.add(engine.world, thoughtBubble);

          // Reset the generation rate limit
          thoughtCooldown = 25; 
        }
      });
    });

    // Draw text dynamically scaled and aligned with each body
    Matter.Events.on(render, "afterRender", () => {
      const context = render.context;
      context.textAlign = "center";
      context.textBaseline = "middle";

      objects.forEach((body) => {
        const { position, angle, labelText, labelColor } = body;

        if (!labelText) return;

        context.save();
        context.translate(position.x, position.y);
        context.rotate(angle);

        // Dynamically adjust font size to match bubble scale as it shrinks
        let fontSize = 22;
        if (body.isThought) {
          const currentRadius = body.circleRadius || 35;
          fontSize = Math.max(8, Math.round(currentRadius * 0.45));
        }

        context.font = `${fontSize}px Arial`;
        context.fillStyle = labelColor || "white";
        context.fillText(labelText, 0, 0);

        context.restore();
      });
    });

    // Cleanup on unmount
    return () => {
      Matter.Render.stop(render);
      Matter.World.clear(engine.world, false);
      Matter.Engine.clear(engine);
      render.canvas.remove();
      render.textures = {};
    };
  }, []);

  return (
    <div
      ref={sceneRef}
      style={{
        width: "100%",
        height: 400,
        background: "transparent",
        position: "relative",
        border: "none",
        outline: "none",
        left: -1
      }}
    >
      <div style={{ height: "10px", width: "100%", position: "absolute", backgroundColor: "#F7F5F2" }} />
    </div>
  );
}